import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { IMAGES } from "@/lib/site";
import { useLang } from "@/context/LanguageContext";

const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 1, delay: 0.5 + i * 0.12, ease: [0.22, 1, 0.36, 1] } }),
};

export const Hero = ({ onDonate, onInvolve }) => {
  const { t, lang } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const flagsY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);

  const lines = [t("hero.l1"), t("hero.l2"), t("hero.l3"), t("hero.l4")];

  return (
    <section ref={ref} data-testid="hero" className="relative bg-white overflow-hidden pt-32 pb-20 md:pt-40 md:pb-28">
      {/* Student-photo background */}
      <div className="absolute inset-0 z-0">
        <img src="/hero-students.jpg" alt="" aria-hidden className="w-full h-full object-cover object-[center_28%]" />
        <div className="absolute inset-0 bg-gradient-to-b from-white/55 via-white/72 to-white" />
      </div>

      <div className="relative z-10 max-w-[1100px] mx-auto px-6 md:px-10 text-center flex flex-col items-center">
        {/* Flags together */}
        <motion.img
          src="/hero-flags.png"
          alt="Haitian and American flags together"
          style={{ y: flagsY }}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="w-64 md:w-[420px] h-auto rounded-2xl shadow-2xl ring-1 ring-black/5"
          data-testid="hero-flags"
        />

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="flex items-center gap-4 mt-6 mb-6"
        >
          <span className="w-8 h-px bg-[#D4AF37]" />
          <span className="text-xs md:text-sm uppercase tracking-[0.25em] font-semibold text-[#B8901F]">{t("hero.badge")}</span>
          <span className="w-8 h-px bg-[#D4AF37]" />
        </motion.div>

        <h1 key={lang} className="font-display text-[#0A1128] text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[0.98] font-medium">
          {lines.map((line, i) => (
            <span key={i} className="hero-line-wrap">
              <motion.span custom={i} variants={lineVariants} initial="hidden" animate="show" className="block">
                {i === 1 || i === 3 ? <span className="italic text-[#B8901F]">{line}</span> : line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.8 }}
          className="mt-8 max-w-2xl text-base md:text-lg text-[#4B5563] leading-relaxed"
        >
          {t("hero.sub")}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5, duration: 0.8 }}
          className="mt-10 flex flex-wrap items-center justify-center gap-4"
        >
          <button data-testid="hero-donate-button" onClick={onDonate} className="group px-8 py-4 bg-[#D4AF37] text-[#0A1128] font-semibold hover:bg-[#E5C158] hover:scale-[1.03] transition-all duration-300">
            {t("hero.donate")}<span className="inline-block ml-2 group-hover:translate-x-1 transition-transform">→</span>
          </button>
          <button data-testid="hero-involve-button" onClick={onInvolve} className="px-8 py-4 border border-[#0A1128]/30 text-[#0A1128] font-medium hover:border-[#D4AF37] hover:text-[#B8901F] transition-all duration-300">
            {t("hero.involved")}
          </button>
        </motion.div>
      </div>
    </section>
  );
};
